import {
  ArrowDownToLine,
  ArrowLeftRight,
  BanknoteArrowDown,
  BanknoteArrowUp,
  CopyIcon,
  Info,
  SendIcon,
  Terminal,
  Split,
  Zap,
} from 'lucide-react'

import { DetailDialogContent } from '~/components/account-detail/DetailDialogContent'
import {
  CommandProps,
  type CommandMenuViewOptions,
} from '~/components/command-menu/types'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { EarnDepositDialog } from '~/components/forms/earn/EarnDialog'
import { buildSendQueryParams } from '~/components/forms/transfer/SendButton'
import { OnrampDialog } from '~/components/smart-accounts/OnrampDialog'
import { TransactionDialogLoadingContent } from '~/components/smart-accounts/TransactionDialogLoadingContent'
import { useEarnDepositBalances } from '~/hooks/useEarnDepositBalances'
import { IAddress } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

// Helper to check if user cannot sign transactions for the current account
const cannotSignTransactions = ({
  smartAccount,
  orgMember,
}: {
  smartAccount?: { signers: Array<{ user?: { id: string } }> }
  orgMember?: { id: string; role?: string }
}) => {
  if (!smartAccount) return false
  if (orgMember?.role === 'OWNER') return false
  return !smartAccount.signers.some(
    (signer) => signer.user?.id === orgMember?.id,
  )
}

type AccountSelectionData = { address: IAddress }
type SetActiveView = Parameters<
  CommandProps['onSelect']
>[0]['actions']['setActiveView']

// Waits for balances so the deposit opens on the network holding the account's USDC
const EarnDepositForAccount = ({
  account,
  setIsOpen,
}: {
  account: IAddress
  setIsOpen: (open: boolean) => void
}) => {
  const { getDepositChainId, isLoading } = useEarnDepositBalances()

  if (isLoading)
    return (
      <SplitsDialogContent id="earnDetails" title="Earn" variant="info">
        <TransactionDialogLoadingContent />
      </SplitsDialogContent>
    )

  return (
    <EarnDepositDialog
      defaultAccount={account}
      chainId={getDepositChainId(account)}
      setIsOpen={setIsOpen}
    />
  )
}

// Uses current address if available, otherwise prompts user to select one
const selectOrPromptForAccount = ({
  address,
  setActiveView,
  onSelectAddress,
  options,
  closeAfterSelect = true,
}: {
  address?: IAddress
  setActiveView: SetActiveView
  onSelectAddress: (selectedAddress: IAddress) => void
  options: CommandMenuViewOptions
  closeAfterSelect?: boolean
}) => {
  if (address) {
    onSelectAddress(address)
    if (closeAfterSelect) setActiveView(null)
    return
  }

  setActiveView(
    'select_account',
    (data) => onSelectAddress((data as AccountSelectionData).address),
    options,
  )
}

export const accountCommands: CommandProps[] = [
  {
    category: 'account',
    label: () => 'Send',
    icon: () => <SendIcon />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      sendTrackingData('commandMenuItemSelected', {
        category: 'account',
        item: 'send',
        address,
      })

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: (selectedAddress) =>
          navigate(`/send?account=${selectedAddress}`),
        options: {
          placeholder: 'Select an account to send from...',
          signersOnly: true,
          primaryActionLabel: 'Send',
        },
      })
    },
    isDisabled: cannotSignTransactions,
    shortcuts: ['n'],
  },
  {
    category: 'account',
    label: () => 'Multisend',
    icon: () => <Split />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      const openMultisendPage = (senderAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'multisend_from_address',
          address: senderAddress,
        })

        return navigate(`/send?account=${senderAddress}&multisend=true`)
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openMultisendPage,
        options: {
          placeholder: 'Select an account to send from...',
          signersOnly: true,
          primaryActionLabel: 'Multisend',
        },
      })
    },
    isDisabled: cannotSignTransactions,
    shortcuts: ['m'],
  },
  {
    category: 'account',
    label: () => 'Swap',
    icon: () => <ArrowLeftRight />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      const openSwapPage = (senderAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'swap_from_address',
          address: senderAddress,
        })

        return navigate(`/swap?account=${senderAddress}`)
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openSwapPage,
        options: {
          placeholder: 'Select an account to swap from...',
          signersOnly: true,
          primaryActionLabel: 'Swap',
        },
      })
    },
    shortcuts: ['s'],
    isDisabled: cannotSignTransactions,
  },
  {
    category: 'account',
    label: () => 'Deposit',
    icon: () => <ArrowDownToLine />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      const openDepositPage = (receiverAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'deposit_to_address',
          address: receiverAddress,
        })

        return navigate(`/deposit?account=${receiverAddress}`)
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openDepositPage,
        options: {
          placeholder: 'Select an account to deposit into...',
          primaryActionLabel: 'Deposit',
        },
      })
    },
    shortcuts: ['d'],
  },
  {
    category: 'account',
    label: () => 'Earn',
    icon: () => <Zap />,
    onSelect: ({
      address,
      actions: { setGlobalModalState, setActiveView },
    }) => {
      const openEarnDialog = (accountAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'earn_from_address',
          address: accountAddress,
        })

        return setGlobalModalState({
          isOpen: true,
          content: (
            <EarnDepositForAccount
              account={accountAddress}
              setIsOpen={() => {
                setGlobalModalState({ isOpen: false, content: null })
              }}
            />
          ),
        })
      }
      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openEarnDialog,
        options: {
          placeholder: 'Select an account for earning...',
          signersOnly: true,
          primaryActionLabel: 'Earn',
        },
      })
    },
    shortcuts: ['e'],
    isDisabled: cannotSignTransactions,
  },
  {
    category: 'account',
    label: () => 'Offramp',
    icon: () => <BanknoteArrowDown />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      const openOfframp = (senderAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'offramp_from_address',
          address: senderAddress,
        })

        // Offramp is folded into the send flow (bank account as recipient).
        return navigate(
          `/send${buildSendQueryParams({ defaultSender: senderAddress })}`,
        )
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openOfframp,
        options: {
          placeholder: 'Select an account to offramp from...',
          primaryActionLabel: 'Offramp',
        },
      })
    },
    shortcuts: ['b'],
    // Offramp rides the send flow and needs a linked bank account to send to.
    isDisabled: ({ orgMember, hasLinkedBankAccount }) =>
      orgMember?.role !== 'OWNER' || !hasLinkedBankAccount,
  },
  {
    category: 'account',
    label: () => 'Onramp',
    icon: () => <BanknoteArrowUp />,
    onSelect: ({ actions: { setGlobalModalState, setActiveView } }) => {
      sendTrackingData('commandMenuItemSelected', {
        category: 'account',
        item: 'onramp',
      })

      setGlobalModalState({
        isOpen: true,
        content: <OnrampDialog />,
      })

      setActiveView(null)
    },
    shortcuts: ['u'],
    isDisabled: ({ orgMember }) => orgMember?.role !== 'OWNER',
  },
  {
    category: 'account',
    label: () => 'Copy address',
    icon: () => <CopyIcon />,
    onSelect: ({ address, actions: { copyToClipboard, setActiveView } }) => {
      const copyAddress = (address: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'copy_address',
          address,
        })

        copyToClipboard(address, 'Address copied')
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: copyAddress,
        options: {
          placeholder: 'Select an account to copy the address of...',
          primaryActionLabel: 'Copy address',
        },
      })
    },
    shortcuts: ['shift+c'],
  },
  {
    category: 'account',
    label: () => 'Custom transaction',
    icon: () => <Terminal />,
    onSelect: ({ address, actions: { navigate, setActiveView } }) => {
      const openCustomTxnPage = (senderAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'custom_txn_from_address',
          address: senderAddress,
        })

        return navigate(`/custom-txn?account=${senderAddress}`)
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openCustomTxnPage,
        options: {
          placeholder: 'Select an account for custom transaction...',
          signersOnly: true,
          primaryActionLabel: 'Custom txn',
        },
      })
    },
    isDisabled: (ctx) => cannotSignTransactions(ctx),
    shortcuts: ['x'],
  },
  {
    category: 'account',
    label: () => 'Show details',
    icon: () => <Info />,
    onSelect: ({
      address,
      actions: { setGlobalModalState, setActiveView },
    }) => {
      const openDetailDialog = (accountAddress: IAddress) => {
        sendTrackingData('commandMenuItemSelected', {
          category: 'account',
          item: 'show_details',
          address: accountAddress,
        })

        return setGlobalModalState({
          isOpen: true,
          content: <DetailDialogContent address={accountAddress} />,
        })
      }

      selectOrPromptForAccount({
        address,
        setActiveView,
        onSelectAddress: openDetailDialog,
        options: {
          placeholder: 'Select an account to show details...',
          primaryActionLabel: 'Show details',
        },
      })
    },
    shortcuts: ['shift+d'],
  },
]
